import { NextSeo } from 'next-seo';
import Head from 'next/head';

//screens
import { About } from '@/screens/Latam/About';

//data-helpers
import { JsonLd } from '@/datafiles/micro-data/';
import { getAwardsLatam } from '@/datafiles/micro-data/organizationLatam';
import { AWARDS_LATAM } from '@/datafiles/awards/latam';
import { getLatamDictionary } from '@/datafiles/languages/latam';
import { buildLatamChrome } from '@/datafiles/nav/latam';
import { OgLocaleAlternates } from '@/components/OgLocaleAlternates';
import { getLocalizedSeo } from '@/datafiles/i18n/seo';
import { getPathFor } from '@/datafiles/i18n/translatedPages';
import { getOpendockUrl } from '@/datafiles/i18n/opendock';

// One body for every LATAM About route (/en/about/, /pt-br/sobre/,
// /es-mx/nosotros/, /es-co/nosotros/): each route file only picks the locale.
export function LatamAboutPage({ locale }) {
  const { about } = getLatamDictionary(locale);
  const { nav, footer } = buildLatamChrome(locale);
  const seo = getLocalizedSeo('latam-about', locale);

  const hero_content = {
    title: about.hero.title,
    titleLine2: about.hero.title_line2,
    description: about.hero.description,
  };

  const offerList_content = {
    label: about.offer_list.label,
    title: about.offer_list.title,
    description: about.offer_list.description,
    linkLabel: about.offer_list.link_label,
    items: about.offer_list.items,
    urls: {
      loadsmart_ai: getPathFor('latam-home', locale, { includePlanned: true }),
      dock_gate_yard: `${getOpendockUrl(locale)}?utm_source=loadsmart-website`,
    },
  };

  const missionBanner_content = {
    title: about.mission_banner.title,
    description: about.mission_banner.description,
  };

  const ourStory_content = {
    title: about.our_story.title,
    description: about.our_story.description,
  };

  const { features } = about.we_in_numbers;
  const weInNumbers_content = {
    title: about.we_in_numbers.title,
    titleLine2: about.we_in_numbers.title_line2,
    features: {
      game_changers: {
        value: features.game_changers.value,
        titleLine1: features.game_changers.title_line1,
        titleLine2: features.game_changers.title_line2,
        description: features.game_changers.description,
      },
      industry_opportunity: {
        value: features.industry_opportunity.value,
        title: features.industry_opportunity.title,
        description: features.industry_opportunity.description,
      },
      series_d_funding: {
        value: features.series_d_funding.value,
        titleLine1: features.series_d_funding.title_line1,
        titleLine2: features.series_d_funding.title_line2,
        description: features.series_d_funding.description,
      },
    },
  };

  const backgroundImageSection_content = {
    title: about.background_image_section.title,
    description: about.background_image_section.description,
    image: {
      xxlarge: 'images/about/become-a-loadie/become_a_loadie_xxlarge@2x.jpg',
      large: 'images/about/become-a-loadie/become_a_loadie_lg@2x.jpg',
      medium: 'images/about/become-a-loadie/become_a_loadie_md@2x.jpg',
      small: 'images/about/become-a-loadie/become_a_loadie_sm@2x.jpg',
    },
    CTA: {
      label: about.background_image_section.cta_label,
      link: 'https://jobs.lever.co/loadsmart/',
      external: true,
    },
    biggerBg: true,
  };

  return (
    <>
      <Head>
        <meta name="keywords" content={about.meta_keywords} />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title={about.meta_title}
        description={about.meta_description}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
        openGraph={{ url: seo.canonical, locale: seo.ogLocale }}
      />
      <OgLocaleAlternates locales={seo.ogLocaleAlternates} />
      <JsonLd data={getAwardsLatam(locale)} />
      <About
        hero_content={hero_content}
        offerList_content={offerList_content}
        missionBanner_content={missionBanner_content}
        ourStory_content={ourStory_content}
        weInNumbers_content={weInNumbers_content}
        backgroundImageSection_content={backgroundImageSection_content}
        awards_content={{ title: about.awards.title }}
        videoUrl={about.video_url}
        awards={AWARDS_LATAM}
        nav={nav}
        footer={footer}
      />
    </>
  );
}
